import type { ExerciseTemplate } from '@yuan-math/exercises';
import { numericExercise, choiceExercise, options, template, trap } from './authoring.ts';

/**
 * Scatter plots and two-way tables.
 *
 * The arithmetic here is slight; the reasoning is not. The distractor that
 * matters is causation read off correlation, which is the single most
 * consequential statistical error a person can carry into adult life. The
 * two-way table adds the second one: a comparison made on raw counts between
 * groups of different sizes, which is how a table is made to say the opposite
 * of what it contains.
 */

/** One survey, in the words each question frame needs. */
type Survey = {
  noun: string;
  rowLabels: [string, string];
  columns: [string, string];
  /** The group, as it reads after "What percentage of the". */
  groupPhrases: [string, string];
  /** The verb phrase, as it reads after a plural subject. */
  doing: string;
  topic: string;
  causal: [string, string];
};

const SURVEYS: Survey[] = [
  {
    noun: 'students',
    rowLabels: ['Year 8', 'Year 9'],
    columns: ['Cycles', 'Does not'],
    groupPhrases: ['Year 8 students', 'Year 9 students'],
    doing: 'cycle to school',
    topic: 'year group and cycling to school',
    causal: [
      'Yes — being in Year 8 is what makes a student cycle',
      'Yes — cycling to school is what puts a student into Year 8',
    ],
  },
  {
    noun: 'people',
    rowLabels: ['Dog owner', 'No dog'],
    columns: ['Walks daily', 'Does not'],
    groupPhrases: ['dog owners', 'people without a dog'],
    doing: 'walk daily',
    topic: 'owning a dog and walking daily',
    causal: [
      'Yes — owning a dog is what makes a person walk daily',
      'Yes — walking daily is what makes a person get a dog',
    ],
  },
  {
    noun: 'students',
    rowLabels: ['Attended', 'Did not attend'],
    columns: ['Passed', 'Did not pass'],
    groupPhrases: ['students who attended the club', 'students who did not attend'],
    doing: 'passed',
    topic: 'attending the club and passing',
    causal: [
      'Yes — the club is what caused the passes',
      'Yes — passing is what made students attend the club',
    ],
  },
];

/**
 * Renders a two-way table as a LaTeX array.
 *
 * `showTotals` is a teaching decision rather than a formatting one. With the
 * margins printed, the work is choosing which of them the question is about;
 * without them, the work is building them. Both are worth asking, and they are
 * not the same question.
 */
function tableLatex(survey: Survey, cells: [[number, number], [number, number]], showTotals: boolean): string {
  const [[a1, b1], [a2, b2]] = cells;
  const header = showTotals
    ? `& \\text{${survey.columns[0]}} & \\text{${survey.columns[1]}} & \\text{Total} \\\\`
    : `& \\text{${survey.columns[0]}} & \\text{${survey.columns[1]}} \\\\`;
  const row = (label: string, a: number, b: number): string =>
    showTotals ? `\\text{${label}} & ${a} & ${b} & ${a + b} \\\\` : `\\text{${label}} & ${a} & ${b} \\\\`;

  return [
    showTotals ? '\\begin{array}{l|cc|c}' : '\\begin{array}{l|cc}',
    header,
    '\\hline',
    row(survey.rowLabels[0], a1, b1),
    row(survey.rowLabels[1], a2, b2),
    ...(showTotals ? ['\\hline', `\\text{Total} & ${a1 + a2} & ${b1 + b2} & ${a1 + a2 + b1 + b2}`] : []),
    '\\end{array}',
  ].join(' ');
}

export const SCATTER_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'scatter-association-direction',
    conceptId: 'scatter-plots',
    description: 'Describe the direction of an association.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const positive = rng.bool();
      const slope = positive ? rng.int(1, 3) : -rng.int(1, 3);
      const intercept = positive ? rng.int(-4, 0) : rng.int(2, 5);
      const points = [-3, -1, 1, 3, 5].map((x, i) => ({
        x,
        y: slope * x + intercept + [0, 1, -1, 1, -1][i]!,
      }));

      const { choices, correctId } = options(
        [
          {
            label: positive ? 'A positive association — as one rises, so does the other' : 'A negative association — as one rises, the other falls',
            correct: true,
          },
          {
            label: positive ? 'A negative association — as one rises, the other falls' : 'A positive association — as one rises, so does the other',
            misconceptionId: 'slope-inverted',
          },
          { label: 'No association at all', misconceptionId: 'slope-inverted' },
          { label: 'One variable must be causing the other', misconceptionId: 'correlation-taken-as-cause' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'What does this scatter plot show?',
        figure: { kind: 'coordinate-grid', min: -8, max: 8, points },
        choices,
        correctId,
        summary: positive
          ? 'The points rise from left to right, so larger values of one variable go with larger values of the other.'
          : 'The points fall from left to right, so larger values of one variable go with smaller values of the other.',
        hint: 'Follow the points from left to right. Do they tend to climb or to fall?',
      });
    },
  }),

  template({
    id: 'scatter-causation',
    conceptId: 'scatter-plots',
    description: 'Refuse to read causation off an association.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const scenario = rng.pick([
        {
          finding: 'Towns with more ice-cream sold also have more sunburn cases.',
          confound: 'hot weather drives both',
          wrong: 'Eating ice cream causes sunburn',
        },
        {
          finding: 'Children with larger shoe sizes tend to read better.',
          confound: 'older children have bigger feet and more schooling',
          wrong: 'Having bigger feet makes a child read better',
        },
        {
          finding: 'Houses with more bedrooms tend to sell for more.',
          confound: 'larger houses have both more rooms and more floor area',
          wrong: 'Adding a bedroom to any house raises its price by the same amount',
        },
      ]);

      const { choices, correctId } = options(
        [
          { label: 'The two are associated, but this does not show one causes the other', correct: true },
          { label: scenario.wrong, misconceptionId: 'correlation-taken-as-cause' },
          { label: 'The association proves a cause, because the pattern is strong', misconceptionId: 'correlation-taken-as-cause' },
          { label: 'There is no relationship worth reporting', misconceptionId: 'slope-inverted' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `${scenario.finding} What can honestly be concluded?`,
        choices,
        correctId,
        summary: `The two move together, but ${scenario.confound} — so a third factor explains the pattern without either variable causing the other.`,
        hint: 'Ask whether something else might be driving both measurements at once.',
      });
    },
  }),

  template({
    id: 'scatter-predict-from-line',
    conceptId: 'scatter-plots',
    description: 'Use a line of best fit to predict a value.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const slope = rng.int(2, difficulty === 4 ? 8 : 5);
      const intercept = rng.int(1, 20);
      const x = rng.int(3, 12);
      const answer = slope * x + intercept;

      return numericExercise({
        prompt: `A line of best fit through a scatter plot has the equation y = ${slope}x + ${intercept}. What does it predict for x = ${x}?`,
        latex: `y = ${slope}x + ${intercept}`,
        figure: { kind: 'coordinate-grid', min: -2, max: 14, lines: [{ slope, intercept, label: 'best fit' }] },
        value: answer,
        summary: `Substituting gives ${slope} × ${x} + ${intercept} = ${answer}.`,
        hint: 'A line of best fit is used exactly like any other linear rule: put the input in and read the output.',
        steps: [
          { label: 'Substitute the input.', latex: `${slope}(${x}) + ${intercept}` },
          { label: 'Work it out.', latex: `${answer}` },
        ],
        traps: [...(intercept * x + slope === answer ? [] : [trap(intercept * x + slope, 'slope-intercept-swapped')])],
      });
    },
  }),

  template({
    id: 'scatter-slope-in-context',
    conceptId: 'scatter-plots',
    description: 'Say what the slope of a line of best fit means, without overclaiming.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const scenario = rng.pick([
        { x: 'hours revised', y: 'test score', unit: 'marks', slope: rng.int(3, 8), intercept: rng.int(20, 45) },
        { x: 'years of age', y: 'price of a used car', unit: 'pounds', slope: -rng.int(300, 900), intercept: rng.int(6000, 12000) },
        { x: 'minutes of exercise a day', y: 'resting heart rate', unit: 'beats a minute', slope: -rng.int(1, 3), intercept: rng.int(70, 85) },
      ]);
      const m = scenario.slope;
      const size = Math.abs(m);
      const direction = m > 0 ? 'higher' : 'lower';

      const { choices, correctId } = options(
        [
          {
            label: `On average, each extra unit of ${scenario.x} goes with a ${size} ${scenario.unit} ${direction} ${scenario.y}`,
            correct: true,
          },
          {
            label: `Each extra unit of ${scenario.x} causes the ${scenario.y} to change by ${size} ${scenario.unit}`,
            misconceptionId: 'correlation-taken-as-cause',
          },
          {
            label: `The ${scenario.y} is ${size} ${scenario.unit} when ${scenario.x} is zero`,
            misconceptionId: 'slope-intercept-swapped',
          },
          {
            label: `The ${scenario.x} changes by ${size} for each extra unit of ${scenario.y}`,
            misconceptionId: 'slope-inverted',
          },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `A line of best fit for ${scenario.y} against ${scenario.x} is y = ${m}x + ${scenario.intercept}. What does the slope mean?`,
        choices,
        correctId,
        summary: `The slope is a rate: how much the ${scenario.y} changes for each extra unit of ${scenario.x}, on average across the data. It is not a promise about any individual, and it is not a claim about cause.`,
        hint: 'A slope is a change in y for a change in x. Two of these describe the intercept or the reciprocal instead, and one claims more than data can show.',
      });
    },
  }),

  /* -------------------------------------------------------- two-way tables */
  template({
    id: 'two-way-table-total',
    conceptId: 'two-way-tables',
    description: 'Build a marginal total from a two-way table.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const survey = rng.pick(SURVEYS);
      const a1 = rng.int(6, 40);
      const b1 = rng.int(6, 40);
      const a2 = rng.int(6, 40);
      const b2 = rng.int(6, 40);
      const columnTotal = a1 + a2;
      const rowTotal = a1 + b1;

      return numericExercise({
        prompt: `How many ${survey.noun} in total ${survey.doing}?`,
        latex: tableLatex(survey, [[a1, b1], [a2, b2]], false),
        unit: survey.noun,
        value: columnTotal,
        summary: `Add down the "${survey.columns[0]}" column: ${a1} + ${a2} = ${columnTotal}.`,
        hint: 'Every cell counts the people who gave both answers. Which way does this question run — across a row, or down a column?',
        steps: [
          { label: `Find the "${survey.columns[0]}" entry for each group.`, latex: `${a1},\\quad ${a2}` },
          { label: 'Add them.', latex: `${a1} + ${a2} = ${columnTotal}` },
        ],
        // Reading across the first row instead of down the first column.
        traps: [...(rowTotal === columnTotal ? [] : [trap(rowTotal, 'two-way-table-margin-misread')])],
      });
    },
  }),

  template({
    id: 'two-way-table-relative-frequency',
    conceptId: 'two-way-tables',
    description: 'Find a relative frequency within a row, where the grand total is the tempting denominator.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const survey = rng.pick(SURVEYS);
      /*
       * A hundred surveyed, so the wrong denominator returns a whole number
       * too. The error has to be caught by reading the question rather than by
       * noticing an ugly answer.
       */
      const group = rng.pick([20, 25, 40, 60, 75, 80]);
      const other = 100 - group;
      const percent = rng.pick([20, 40, 60, 80]);
      const otherPercent = rng.pick([20, 40, 60, 80]);
      const count = (group * percent) / 100;
      const otherCount = (other * otherPercent) / 100;

      return numericExercise({
        prompt: `100 ${survey.noun} were surveyed. What percentage of the ${survey.groupPhrases[0]} ${survey.doing}?`,
        latex: tableLatex(survey, [[count, group - count], [otherCount, other - otherCount]], true),
        unit: '%',
        value: percent,
        summary: `The question is about the ${survey.groupPhrases[0]}, so they are the whole: ${count} out of ${group} is ${percent}%.`,
        hint: 'Say the question aloud. Whichever group is named after "percentage of" is what goes underneath.',
        steps: [
          { label: 'Take the count and the total for that group.', latex: `\\frac{${count}}{${group}}` },
          { label: 'Convert to a percentage.', latex: `\\frac{${count}}{${group}} = ${percent}\\%` },
        ],
        // Dividing by the grand total of 100 instead, which just returns the count.
        traps: [...(count === percent ? [] : [trap(count, 'relative-frequency-wrong-total')])],
      });
    },
  }),

  template({
    id: 'two-way-table-association',
    conceptId: 'two-way-tables',
    description: 'Judge association from a table where the counts and the shares disagree.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const survey = rng.pick(SURVEYS);
      const associated = rng.bool();

      /*
       * Both cases are built so the counts and the shares tell different
       * stories: where there is an association the two counts are *equal*, and
       * where there is none they are far apart. A student comparing raw counts
       * between groups of different sizes is therefore wrong either way, which
       * is the entire point of the topic.
       */
      const same = rng.pick([20, 40, 60, 80]);
      const config = associated
        ? rng.pick([
            { group: 25, percent: 60, otherPercent: 20 },
            { group: 40, percent: 60, otherPercent: 40 },
            { group: 20, percent: 60, otherPercent: 15 },
            { group: 75, percent: 20, otherPercent: 60 },
            { group: 60, percent: 40, otherPercent: 60 },
            { group: 80, percent: 15, otherPercent: 60 },
          ])
        : { group: rng.pick([20, 25, 40, 60, 75, 80]), percent: same, otherPercent: same };

      const other = 100 - config.group;
      const count = (config.group * config.percent) / 100;
      const otherCount = (other * config.otherPercent) / 100;

      const { choices, correctId } = options(
        [
          {
            label: associated
              ? `Yes — ${config.percent}% of the ${survey.groupPhrases[0]} against ${config.otherPercent}% of the ${survey.groupPhrases[1]}`
              : `No — ${config.percent}% of each group, even though the counts differ`,
            correct: true,
          },
          {
            label: associated
              ? `No — both groups have the same number, ${count} each`
              : `Yes — ${count} against ${otherCount} is a clear difference`,
            misconceptionId: 'relative-frequency-wrong-total',
          },
          { label: survey.causal[0], misconceptionId: 'correlation-taken-as-cause' },
          { label: survey.causal[1], misconceptionId: 'correlation-taken-as-cause' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `100 ${survey.noun} were surveyed. Does this table suggest an association between ${survey.topic}?`,
        latex: tableLatex(survey, [[count, config.group - count], [otherCount, other - otherCount]], true),
        choices,
        correctId,
        summary: associated
          ? `The groups are different sizes, so the counts — ${count} and ${otherCount} — compare nothing at all. As a share of each group it is ${config.percent}% against ${config.otherPercent}%, which is a real difference: an association, and no more than that.`
          : `The counts differ, ${count} against ${otherCount}, only because the groups are different sizes. As a share of each group both are ${config.percent}%, so knowing the group tells you nothing.`,
        hint: 'Turn each row into a percentage of that row before you compare anything.',
      });
    },
  }),
];
